import { storageManager } from '../storage/storageManager';
import { STORAGE_KEYS } from '../storage/storageKeys';
import type { AppleSignInResult } from './AppleAuthCore';

export type StoredAppleSignInProfile = {
  email?: string;
  givenName?: string;
  familyName?: string;
  fullName?: string;
};

function buildStoredFullName(profile: AppleSignInResult | StoredAppleSignInProfile): string | undefined {
  return (
    profile.fullName?.trim() ||
    [profile.givenName, profile.familyName].filter(Boolean).join(' ').trim() ||
    undefined
  );
}

export async function saveAppleSignInProfile(profile: AppleSignInResult | StoredAppleSignInProfile): Promise<void> {
  const fullName = buildStoredFullName(profile);

  const stored: StoredAppleSignInProfile = {
    email: profile.email?.trim() || undefined,
    givenName: profile.givenName?.trim() || undefined,
    familyName: profile.familyName?.trim() || undefined,
    fullName: fullName || undefined,
  };

  const hasName = Boolean(stored.fullName || stored.givenName || stored.familyName);
  if (!stored.email && !hasName) return;

  try {
    await storageManager.set(STORAGE_KEYS.APPLE_SIGNIN_PROFILE, stored);
    console.log('[AppleSignIn] saved to storage:', stored);
  } catch (err) {
    console.error('[AppleSignIn] Failed to persist Apple sign-in profile:', err);
  }
}

export async function getAppleSignInProfile(): Promise<StoredAppleSignInProfile | null> {
  try {
    const stored = await storageManager.get<StoredAppleSignInProfile>(STORAGE_KEYS.APPLE_SIGNIN_PROFILE);
    if (!stored) {
      console.log('[AppleSignIn] read from storage: (none)');
      return null;
    }
    const fullName = buildStoredFullName(stored);
    const hydrated = {
      email: stored.email?.trim() || undefined,
      givenName: stored.givenName?.trim() || undefined,
      familyName: stored.familyName?.trim() || undefined,
      fullName: fullName || undefined,
    };
    console.log('[AppleSignIn] read from storage:', hydrated);
    return hydrated;
  } catch {
    console.warn('[AppleSignIn] read from storage failed');
    return null;
  }
}

export async function clearAppleSignInProfile(): Promise<void> {
  try {
    await storageManager.remove(STORAGE_KEYS.APPLE_SIGNIN_PROFILE);
  } catch {
    // ignore
  }
}
